import { Stack, Text, Button, Group, Checkbox, Skeleton } from '@mantine/core';
import BigNumber from 'bignumber.js';
import { useState } from 'react';
import cn from 'clsx';
import { WalletSuspense } from 'src/components/WalletSuspense/WalletSuspense';
import { ErrorDisplay } from 'src/components/ErrorDisplay/ErrorDisplay';
import { Rows } from 'src/components/Rows/Rows';
import { FilterButtons } from 'src/components/FilterButtons/FilterButtons';
import { Select } from 'src/components/Select/Select';
import { useWalletRequests } from 'src/api';
import { useStakingIsPaused } from 'src/api/smart-contracts';
import { LatestRequest } from 'src/types';
import { formatDecimal } from 'src/utils';
import {
  SHOW_GAS_FEES,
  ALWAYS_SHOW_STAKING_STATUS_BANNER,
} from 'src/constants';
import { ClaimRequest } from './ClaimRequest';
import { useRedeem } from './useRedeem';

export function ClaimPanel({
  heading,
  latestRequest,
}: {
  heading: React.ReactNode;
  latestRequest: LatestRequest | null;
}) {
  return (
    <Stack gap={24}>
      <WalletSuspense
        fallback={
          <>
            <Group justify="center">{heading}</Group>
            <Text ta="center">
              Connect wallet to see your withdrawal requests.
            </Text>
          </>
        }
      >
        <RequestsList heading={heading} latestRequest={latestRequest} />
      </WalletSuspense>

      <WalletSuspense buttonProps={{ size: 'lg' }} />
    </Stack>
  );
}

const FILTER_ITEMS = [
  { label: 'All', value: 'all' },
  { label: 'Ready to claim', value: 'claimable' },
  { label: 'Pending', value: 'pending' },
];

function RequestsList({
  heading,
  latestRequest,
}: {
  heading: React.ReactNode;
  latestRequest: LatestRequest | null;
}) {
  const {
    walletRequests: _walletRequests,
    isLoadingWalletRequests,
    walletRequestsError,
  } = useWalletRequests();

  const isOptimisticInjectionNeeded =
    !!latestRequest &&
    !_walletRequests.filter(
      (r) =>
        BigNumber(r.requested_at || 0).gte(latestRequest.timestamp) &&
        BigNumber(r.assets).eq(latestRequest.value)
    ).length;

  const walletRequests = isOptimisticInjectionNeeded
    ? [
        {
          assets: latestRequest.value,
          shares: latestRequest.value,
          requested_at: latestRequest.timestamp,
          id: '-1',
          claimed: false,
          is_claimable: false,
        },
        ..._walletRequests,
      ]
    : _walletRequests;

  const [activeFilter, setActiveFilter] = useState<
    (typeof FILTER_ITEMS)[number]['value']
  >(FILTER_ITEMS[0].value);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [redeemIds, setRedeemIds] = useState<string[]>([]);
  const [redeemedIds, setRedeemedIds] = useState<string[]>([]);
  const [redeemedHashes, setRedeemedHashes] = useState<Record<string, string>>(
    {}
  );

  const { stakingIsPaused, stakingIsPausedMessage } = useStakingIsPaused();

  const {
    transactionCost,
    isLoadingTransactionCost,
    isRedeeming,
    redeemError,
    redeem,
  } = useRedeem({
    requestIds: selectedIds,
    onSuccess: (hash?: string) => {
      setRedeemedIds([...redeemedIds, ...redeemIds]);
      setRedeemedHashes({
        ...redeemedHashes,
        ...redeemIds.reduce((acc, id) => ({ ...acc, [id]: hash }), {}),
      });
      setRedeemIds([]);
      setSelectedIds([]);
    },
  });

  const claimTotalAmount = selectedIds.reduce((acc, id) => {
    const request = walletRequests.find((r) => r.id === id);
    return acc.plus(request ? request.assets : 0);
  }, BigNumber(0));

  const claimableRequests = walletRequests.filter(
    (r) => r.is_claimable && !r.claimed && !redeemedIds.includes(r.id)
  );
  const pendingRequests = walletRequests.filter(
    (r) =>
      !r.claimed &&
      !r.is_claimable &&
      !redeemedIds.includes(r.id) &&
      !redeemIds.includes(r.id)
  );
  const unclaimedRequests = walletRequests.filter(
    (r) => !r.claimed || redeemedIds.includes(r.id)
  );

  const claimableAmount = claimableRequests.reduce(
    (acc, r) => acc.plus(r.assets),
    BigNumber(0)
  );

  const allSelected = selectedIds.length === claimableRequests.length;

  const filteredRequests =
    activeFilter === 'claimable'
      ? claimableRequests
      : activeFilter === 'pending'
        ? pendingRequests
        : unclaimedRequests;

  const footerItems = [
    ...(SHOW_GAS_FEES
      ? [
          {
            label: 'Estimated transaction cost',
            value: isLoadingTransactionCost ? (
              <Skeleton w={160} h={23} />
            ) : !selectedIds.length ? (
              '-'
            ) : (
              `${formatDecimal(transactionCost, { maxDecimals: 6 })} MON`
            ),
          },
        ]
      : []),
  ];

  const submitDisabled =
    stakingIsPaused || isRedeeming || !redeem || !selectedIds.length;

  const handleSubmit = () => {
    if (submitDisabled) {
      return;
    }

    setRedeemIds(selectedIds);
    redeem();
  };

  const renderRequests = () => {
    if (isLoadingWalletRequests) {
      return (
        <Text my={80} ta="center">
          Loading...
        </Text>
      );
    }

    if (walletRequestsError && !ALWAYS_SHOW_STAKING_STATUS_BANNER) {
      return (
        <Stack>
          <Text ta="center">
            Oops, something went wrong. Try refreshing the page.
          </Text>
          <ErrorDisplay>{walletRequestsError.message}</ErrorDisplay>
        </Stack>
      );
    }

    if (!filteredRequests.length) {
      return (
        <Text ta="center">
          No{' '}
          {activeFilter === 'claimable'
            ? 'claimable'
            : activeFilter === 'pending'
              ? 'pending'
              : ''}{' '}
          requests found.
        </Text>
      );
    }

    return filteredRequests.map((request) => {
      const isChecked = selectedIds.includes(request.id);

      return (
        <ClaimRequest
          key={request.id}
          request={request}
          hash={redeemedHashes[request.id]}
          isChecked={isChecked}
          isRedeeming={isRedeeming && redeemIds.includes(request.id)}
          isRedeemed={redeemedIds.includes(request.id)}
          onCheck={() =>
            setSelectedIds(
              isChecked
                ? selectedIds.filter((id) => id !== request.id)
                : [...selectedIds, request.id]
            )
          }
        />
      );
    });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      className={cn((stakingIsPaused || isRedeeming) && 'pointer-events-none')}
    >
      <Stack gap={24}>
        <Group
          justify={!!unclaimedRequests.length ? 'space-between' : 'center'}
        >
          {heading}

          {!!unclaimedRequests.length && (
            <Group gap={8}>
              <Text c="var(--mantine-color-main-2)">Available to claim</Text>

              <span
                className={cn(
                  'h-[40px] leading-[40px] px-4',
                  'bg-primarypurple/50 rounded-[90px] text-xs font-[700]',
                  isLoadingWalletRequests && 'animate-pulse'
                )}
              >
                {isLoadingWalletRequests ? (
                  <span className="block w-20" />
                ) : (
                  <>{formatDecimal(claimableAmount.div(1e18).toFixed())} MON</>
                )}
              </span>
            </Group>
          )}
        </Group>

        {!!unclaimedRequests.length && (
          <>
            <div className="h-[1px] bg-white/[.14]" />

            <Group justify="space-between">
              <Checkbox
                label="Select All"
                checked={!!claimableRequests.length && allSelected}
                size="md"
                pl={16}
                disabled={!claimableRequests.length}
                classNames={{ label: 'pl-2 lg:pl-4 text-xs' }}
                onChange={() =>
                  allSelected
                    ? setSelectedIds([])
                    : setSelectedIds(claimableRequests.map((r) => r.id))
                }
              />

              <FilterButtons
                data={FILTER_ITEMS}
                value={activeFilter}
                compact
                segControlProps={{ className: 'max-lg:hidden' }}
                onChange={setActiveFilter}
              />
              <Select
                options={FILTER_ITEMS}
                value={activeFilter}
                stackProps={{ className: 'lg:hidden' }}
                // @ts-expect-error
                comboboxProps={{ w: 160 }}
                onChange={setActiveFilter}
              />
            </Group>
          </>
        )}

        <Stack
          gap={!!filteredRequests.length ? 48 : 24}
          className={cn(isRedeeming && 'pointer-events-none')}
        >
          <Stack gap={24}>{renderRequests()}</Stack>

          <Stack gap={24}>
            <Button
              variant="gradient"
              size="lg"
              type="submit"
              disabled={submitDisabled}
              loading={isRedeeming}
            >
              {stakingIsPaused
                ? stakingIsPausedMessage
                : `Claim ${formatDecimal(claimTotalAmount.div(1e18).toFixed())} MON`}
            </Button>

            <ErrorDisplay>{redeemError?.message}</ErrorDisplay>

            {!!footerItems.length && (
              <>
                <div className="h-[1px] bg-main1 opacity-20" />

                <Stack gap={8}>
                  <Rows items={footerItems} />
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
    </form>
  );
}
